/** useOnceVisible — "has this element been on screen yet?", latched.
 *
 * Built for the record timeline (E1b-2 left a note asking for exactly this): the panel's lanes fetch
 * when the RECORD opens rather than when a tab is clicked, so a ‹ › hop through records pays four
 * governed lane requests — three of them AUDITED — for a section the operator may never scroll to.
 * Gating each lane's `enabled` on this hook turns those into reads that happen when someone actually
 * looks, which is also the honest reading of the audit row.
 *
 * THREE PROPERTIES ARE LOAD-BEARING:
 *
 * · **Latched.** Once true it never goes back to false. A lane that unmounted its query on scroll-out
 *   would refetch (and re-audit) on scroll-in — turning one read into N.
 * · **Fails OPEN.** No `IntersectionObserver` (jsdom, an old browser, a stubbed test env) ⇒ visible
 *   from the first render. A deferral primitive must never be the reason a governed surface renders
 *   empty; the worst case is the behaviour we had before it existed.
 * · **Pre-fetches by a margin.** The default `rootMargin` starts the fetch before the section reaches
 *   the viewport, so scrolling to it usually finds content rather than a spinner.
 *
 * ⛔ THE LATCH IS PER `resetKey`, AND A CONSUMER THAT HOSTS MORE THAN ONE SUBJECT MUST PASS ONE.
 * The latch is per-INSTANCE, and the record panel's timeline does not remount when `entryId` changes —
 * so without a reset key the first scroll-to-timeline latches the hook for the whole session and every
 * SUBSEQUENT record in a ‹ › hop fires all four governed lane reads on open. That is not a cosmetic
 * regression: it silently narrows the audit claim to a falsehood ("this read means someone scrolled to
 * the timeline" becomes true of the first record only), which is the one thing D1 exists to make true.
 * Pass the host identity (`resetKey={entryId}`) and the latch re-arms per record.
 *
 * ⛔ THE RESET HAPPENS DURING RENDER, NOT IN AN EFFECT — for the same reason `openBodies` is scoped
 * during render in `CrmRecordTimeline` (see the `openBodies` declaration there: "the effect version is
 * one render too late", measured — it left the body hook called twice for one click).
 * An effect runs AFTER the render for the new record, by which time `visible === true` has already been
 * handed to four `enabled` flags and four requests are in flight for a section nobody has scrolled to.
 * State is therefore shaped `{ key, visible }` and a key mismatch is treated as not-visible in the
 * render that observes it.
 *
 * After a reset the effect builds a FRESH observer. `IntersectionObserver` delivers an initial entry on
 * `observe()`, so a section that is still on screen re-latches within a tick — which is correct: the
 * operator IS looking at the new record's timeline.
 *
 * · **`skip` LATCHES; it does not toggle off.** A caller that flips `skip` false → true after mount
 *   becomes visible immediately (also during render). Going back to `false` does NOT un-latch — "I
 *   have decided I want everything now" is not a reversible statement on an audited read.
 * · **`root`.** Per the IntersectionObserver spec the target's ancestor clipping is applied BEFORE the
 *   intersection is taken against the rootMargin-expanded root rect, so inside the record panel's own
 *   `overflow-y: auto` container the 200px margin against the implicit viewport root is INERT — the
 *   element is already clipped away by the scroller. A consumer that wants real pre-fetch lead has to
 *   pass that scroll container as `root`.
 *
 * The ref goes on the element whose visibility matters (for a section: its own wrapper — the header
 * gives it height before any lane has answered). ⚠ It must be attached in the element's FIRST render:
 * the observer is built once per (key, visible=false) and there is no re-arm on a late attach, so a
 * conditionally-rendered target would never arm at all. Change `resetKey` if you need a fresh attempt.
 */
import { useEffect, useRef, useState } from 'react';

/** How far outside the viewport counts as "coming up" — one screenful of a laptop, roughly. */
const DEFAULT_ROOT_MARGIN = '200px';

interface Options {
  rootMargin?: string;
  /** Resolve to visible immediately and never observe. Latches — see the header. */
  skip?: boolean;
  /** The scroll container to intersect against. Omit for the viewport. See the header for why the
   * default makes `rootMargin` inert inside a scrolling panel. */
  root?: Element | null;
  /** The subject this latch belongs to (for the record panel: `entryId`). Changing it un-latches
   * DURING RENDER and re-arms the observer. Omit only when the host truly has one subject for its
   * whole lifetime — see the ⛔ in the header for what a missing key costs. */
  resetKey?: unknown;
}

export function useOnceVisible<T extends HTMLElement>(
  { rootMargin = DEFAULT_ROOT_MARGIN, skip = false, root, resetKey }: Options = {},
) {
  const ref = useRef<T | null>(null);
  // `skip` (a caller that has decided it wants everything now) and a missing observer both resolve to
  // "visible", and they resolve during the FIRST render rather than in an effect: an effect-driven
  // fallback would paint one frame of the not-yet-visible branch, which on this surface is an empty
  // state, and an empty state that flickers reads as "this deal has no history".
  const supported = typeof IntersectionObserver !== 'undefined';
  const [state, setState] = useState<{ key: unknown; visible: boolean }>(
    () => ({ key: resetKey, visible: skip || !supported }),
  );

  // React's sanctioned "adjust state when a prop changes": it re-renders before committing, so no
  // consumer ever sees the previous subject's latch, and unlike an effect it costs no extra fetch.
  let visible = state.visible;
  if (!Object.is(state.key, resetKey)) {
    visible = skip || !supported;
    setState({ key: resetKey, visible });
  }
  if (skip && !visible) {
    // Read in render, not only in the useState initializer: a `skip` that turns true after mount was
    // silently ignored before, so a caller that decided mid-life to stop deferring kept waiting.
    visible = true;
    setState({ key: resetKey, visible: true });
  }

  useEffect(() => {
    if (visible) return;                       // latched — nothing left to observe
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setState({ key: resetKey, visible: true });
    }, { root: root ?? null, rootMargin, threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible, rootMargin, root, resetKey]);

  return { ref, visible } as const;
}

export default useOnceVisible;
